/**
 * Tuning constants — transcribed from docs/design/01-constants.md (draft,
 * sprint 1). Key names match the design table exactly; values are design's
 * provisional numbers and get tuned there first, then re-transcribed here.
 *
 * Sections K (bot) and L (audio/fx) are not sim constants and live elsewhere.
 * The structured objects at the bottom (ARENA/PLAYER/DISC/TICKS) are derived
 * views used by the sprint-1 step skeleton; they add no new numbers.
 */

// ── A. Simulation & networking ──────────────────────────────────────────────
export const SIM_HZ = 60
export const SIM_DT = 1 / SIM_HZ
export const SNAPSHOT_HZ = 30
export const INPUT_SEND_HZ = 60
export const INPUT_REDUNDANCY = 3
export const INTERP_DELAY_MS = 100
export const INTERP_EXTRAP_MAX_MS = 50
export const INPUT_REWIND_MAX_TICKS = 12
export const INPUT_HOLD_MAX_TICKS = 6
export const HISTORY_TICKS = 64
export const PREDICTION_MAX_AHEAD_TICKS = 15
export const RECONCILE_SNAP_DIST = 1.5
export const RECONCILE_SMOOTH_TICKS = 6
export const CLOCK_SYNC_INTERVAL_S = 2
export const RECONNECT_GRACE_S = 15
export const MAX_PLAYERS_PER_MATCH = 4
export const MAX_TEAM_SIZE = 2

// ── B. Arena (defaults for arena `corridor_a`) ──────────────────────────────
export const ARENA_LENGTH = 30
export const ARENA_WIDTH = 12
export const ZONE_DEPTH = 10
export const GAP_START = 10
export const GAP_END = 20
export const WALL_HEIGHT = 3
export const PILLAR_COUNT = 2
export const PILLAR_X = 2.4 // pillars at (±PILLAR_X, PILLAR_Z)
export const PILLAR_Z = 15
export const PILLAR_RADIUS = 0.7
export const PILLAR_HEIGHT = 3
export const FLOOR_Y = 0
export const DISC_FLIGHT_Y = 1.1
export const GRAVITY = 20 // reserved
export const VOID_KILL_Y = -3 // reserved
export const SPAWN_BACK_OFFSET = 3
export const SPAWN_X_SOLO = 0
export const SPAWN_X_PAIR = 2.5 // ±
export const OT_SHRINK_RATE = 0.25
export const OT_ZONE_MIN_DEPTH = 3
export const OT_ZONE_MIN_WIDTH = 4
export const BOUNDS_PUSH_SPEED = 4

// ── B2. Open-world hub arena `openworld_a` (spec 2026-09-03) ────────────────
export const OPENWORLD_SIZE = 600
export const OPENWORLD_MOVE_MUL = 1.5
export const LANDMARK_CELL = 60
export const LANDMARK_CLEAR_RADIUS = 25
export const TERMINAL_USE_RADIUS = 2.5

// ── C. Camera / projection (client render inputs) ───────────────────────────
export const VIRTUAL_W = 1280
export const VIRTUAL_H = 720
export const CAM_BACK = 7
export const CAM_HEIGHT = 6
export const CAM_PITCH_DEG = 16
export const CAM_HFOV_DEG = 62
export const CAM_NEAR = 0.5

// ── D. Player movement & stamina ────────────────────────────────────────────
export const PLAYER_RADIUS = 0.4
export const PLAYER_HEIGHT = 1.8
export const MOVE_SPEED = 6.5
export const MOVE_ACCEL = 45
export const MOVE_DECEL = 60
export const SHIELD_MOVE_MULT = 0.4
export const DASH_SPEED = 16
export const DASH_TICKS = 12
export const DASH_COST = 35
export const DASH_COOLDOWN_TICKS = 18
export const STAMINA_MAX = 100
export const STAMINA_REGEN = 25
export const STAMINA_REGEN_DELAY_TICKS = 36
export const EXHAUST_TICKS = 60
export const COUNTDOWN_MOVE_ALLOWED = true
// Jump-dodge (Sprint 10 spec §1.1). GRAVITY (§B) is the integrator's g.
export const JUMP_V0 = 7.6 // m/s; apex = v0²/(2·GRAVITY) = 1.444 m, airtime = 2·v0/g = 0.76 s (46 ticks)
export const JUMP_COST = 25 // stamina; between shield raise (5) and dash (35)
export const JUMP_LAND_TICKS = 6 // 100 ms landing lock
export const JUMP_CLEAR_MARGIN = 0 // m above the disc's top edge before the hurtbox is skipped (03 §4)

// ── E. Shield (held deflector) ──────────────────────────────────────────────
export const SHIELD_RAISE_COST = 5
export const SHIELD_MIN_RAISE = 15
export const SHIELD_DRAIN = 30
export const SHIELD_PERFECT_TICKS = 8
export const SHIELD_RADIUS = 0.75
export const SHIELD_ARC_HALF_DEG = 55
export const SHIELD_DROP_LOCKOUT_TICKS = 6
export const DEFLECT_PERFECT_SPEED_MULT = 1.1
export const DEFLECT_PERFECT_REFUND = 10
export const DEFLECT_LATE_SPEED_MULT = 0.7
export const DEFLECT_LATE_COST = 20

// ── F. Throw / catch / pickup ───────────────────────────────────────────────
export const THROW_WINDUP_TICKS = 3
export const THROW_RECOVERY_TICKS = 10
export const THROW_SHIELD_LOCK_TICKS = 6
export const THROW_ORIGIN_OFFSET = 0.6
export const CATCH_RADIUS = 0.9
export const PICKUP_RADIUS = 0.7
export const MAX_DISCS_HELD_BASE = 1 // DECISION NEEDED (D1)
export const MAX_DISCS_HELD_CAP = 3
export const MAX_DISCS_IN_WORLD = 27

// ── G. Disc flight ──────────────────────────────────────────────────────────
export const DISC_RADIUS = 0.22
export const DISC_THROW_SPEED = 24 // 01 §G. 24→27 (sprint-3 bench gate) reverted in the sprint-4 review; moves only on human playtest data (13 §4)
export const DISC_RANGE = 50
export const DISC_MAX_BOUNCES = 3
export const DISC_BOUNCE_RESTITUTION = 0.92
export const DISC_MIN_FLIGHT_SPEED = 12
export const DISC_RETURN_SPEED = 16
export const DISC_RETURN_TURN_RATE = 540
export const DISC_RETURN_TIMEOUT_TICKS = 150
export const DISC_GROUND_TIMEOUT_TICKS = 360
export const DISC_GROUND_SLIDE_DECEL = 30
export const DISC_CLASH_ENABLED = true
export const FRIENDLY_FIRE = false // DECISION NEEDED (D4)
export const SELF_HIT = false

// ── H. Round / match / hub flow ─────────────────────────────────────────────
export const ROUNDS_TO_WIN = 2
export const MAX_VOID_ROUNDS = 2
export const ROUND_TIME_S = 90
export const OVERTIME_S = 45
export const COUNTDOWN_TICKS = 180
export const ROUND_END_FREEZE_TICKS = 150
export const BETWEEN_ROUNDS_S = 4
export const RESULTS_SCREEN_S = 12
export const PICK_DECK_S = 20
export const PICK_LOCK_S = 10
export const HITSTOP_MS = 200
export const HUB_RESPAWN_S = 2
export const HUB_SPAWN_INVULN_S = 2.5
export const HUB_QUEUE_MIN = 2
export const HUB_QUEUE_FILL_WAIT_S = 10
export const HUB_QUEUE_RESET_AFTER_MATCH = true
export const HUB_CAPACITY = 8
export const HUB_EMPTY_TTL_S = 60
export const HUB_SPAWN_X: readonly number[] = [-4.5, -1.5, 1.5, 4.5]
export const HUB_BOT_MAX_HUMANS = 1
export const HUB_BOT_DESPAWN_HUMANS = 3
export const HUB_IDLE_KICK_S = 600
export const HUB_IDLE_UNFLAG_S = 120
export const SPECTATORS_PER_MATCH = 16
export const SPECTATE_EXTRA_DELAY_MS = 0 // reserved
export const KIOSK_LAST_RESULT_S = 60
export const DRAIN_MATCH_CAP_S = 300
export const RESUME_COUNTDOWN_TICKS = 120
export const DROP_DETECT_S = 5

// ── J. Power-up hooks (base values; 06 overrides per player) ────────────────
export const DASH_IFRAME_TICKS = 0
export const DISC_SPEED_CAP_MULT = 1.3
export const DISC_SHIELD_PIERCE = false // reserved
export const INTERCEPT_ENABLED = false // reserved
export const POWERUP_SLOTS = 1

/** 06 §4 REVEAL phase length (2 s). Not in 01's table; lives with the lobby rule. */
export const REVEAL_TICKS = 2 * SIM_HZ

// ── Engine aliases (older names used across server/client plumbing) ─────────
export const TICK_RATE = SIM_HZ
export const TICK_DT = SIM_DT
export const DEFAULT_SNAPSHOT_HZ = SNAPSHOT_HZ
export const MAX_REWIND_TICKS = INPUT_REWIND_MAX_TICKS
export const MAX_REWIND_MS = (INPUT_REWIND_MAX_TICKS / SIM_HZ) * 1000
export const INPUT_RING_CAPACITY = 256
export const DEFAULT_INTERP_DELAY_MS = INTERP_DELAY_MS

// ── Derived views for the sprint-1 step skeleton ────────────────────────────
// The skeleton implements a simplified subset of 03/04 (timed block instead of
// the held shield, no windup/recovery, no accel). These objects only re-group
// the keys above so the skeleton reads cleanly; the full state machines land
// in sprint 2 and should read the flat keys directly.
export const ARENA = {
  width: ARENA_WIDTH,
  length: ARENA_LENGTH,
  gapStart: GAP_START,
  gapEnd: GAP_END,
  pillarRadius: PILLAR_RADIUS,
  pillarOffsetX: PILLAR_X,
  pillarZ: PILLAR_Z,
  floorY: FLOOR_Y,
  spawnBackOffset: SPAWN_BACK_OFFSET,
} as const

export const PLAYER = {
  radius: PLAYER_RADIUS,
  moveSpeed: MOVE_SPEED,
  dashSpeed: DASH_SPEED,
  dashStaminaCost: DASH_COST,
  blockStaminaCost: DEFLECT_LATE_COST,
  blockMoveMultiplier: SHIELD_MOVE_MULT,
  staminaMax: STAMINA_MAX,
  staminaRegenPerSec: STAMINA_REGEN,
  catchRadius: CATCH_RADIUS,
  /** Skeleton: a disc within shield reach while the block window is open deflects. */
  deflectRadius: SHIELD_RADIUS + DISC_RADIUS,
} as const

export const DISC = {
  radius: DISC_RADIUS,
  flightY: DISC_FLIGHT_Y,
  throwSpeed: DISC_THROW_SPEED,
  returnSpeed: DISC_RETURN_SPEED,
  maxRange: DISC_RANGE,
  maxBounces: DISC_MAX_BOUNCES,
  restitution: DISC_BOUNCE_RESTITUTION,
  perPlayer: MAX_DISCS_HELD_BASE,
} as const

export const TICKS = {
  dash: DASH_TICKS,
  dashCooldown: DASH_COOLDOWN_TICKS,
  block: SHIELD_PERFECT_TICKS,
  blockRecovery: SHIELD_DROP_LOCKOUT_TICKS,
  respawn: Math.round(HUB_RESPAWN_S * SIM_HZ),
  ownerGrace: 6,
  looseTimeout: DISC_GROUND_TIMEOUT_TICKS,
} as const
